import { t } from '@lingui/core/macro'
import type {
  DataPreviewResponse,
  TrainResponse,
} from 'features/timeSeries/experiment/types'
import type { SentinelBandOverlay } from 'features/timeSeries/hooks/useSentinelScores'

/**
 * The experiment band overlay is the sentinel band overlay minus the color:
 * the color is resolved chart-side from the target pen, exactly like
 * sentinel bands.
 */
export type ExperimentBandOverlay = Omit<SentinelBandOverlay, 'color'>

/** Points the cleaning plan removed for one tag, as chart marker series. */
export interface ExperimentRemovedPoints {
  sensorId: string
  /** Epoch-ms x positions, ascending. */
  xs: number[]
  values: (number | null)[]
  /** Parallel to xs: "value_op" | "gate" when the service reports it. */
  reasons?: string[]
}

const MS_PER_UNIT: Record<string, number> = {
  seconds: 1_000,
  minutes: 60_000,
  hours: 3_600_000,
}

/**
 * Milliseconds of feature warm-up at the start of the train window: the
 * model's lag/rolling features don't exist for the first
 * max(driver_lags, rolling_window) native-interval rows, so its predictions
 * there are center-fill artifacts, not expectations. Derived from the
 * response's resolved config so it survives config drift; falls back to the
 * default knobs (lags 1,3,6 / rolling 6 / 10-minute interval).
 *
 * The native interval comes from window.interval + interval_unit — the
 * response only fills window.interval_minutes for minute-unit runs (it's
 * null for second/hour intervals), which previously defaulted the math to
 * 10 minutes and trimmed the wrong span for non-minute experiments.
 */
function warmupMs(result: TrainResponse): number {
  const resolved = result.resolved_config as
    | {
        feature?: { driver_lags?: number[]; rolling_window?: number }
        interval_minutes?: number
      }
    | undefined
  const lags = resolved?.feature?.driver_lags?.filter(Number.isFinite) ?? [
    1, 3, 6,
  ]
  const rolling = resolved?.feature?.rolling_window ?? 6
  const periods = Math.max(0, ...lags, rolling)

  const window = result.window
  const unitMs = MS_PER_UNIT[window?.interval_unit ?? ''] ?? null
  const intervalMs =
    window?.interval && unitMs
      ? window.interval * unitMs
      : (window?.interval_minutes ?? resolved?.interval_minutes ?? 10) * 60_000
  return periods * intervalMs
}

/**
 * Median positive spacing between consecutive xs, or null when there aren't
 * two distinct timestamps. The train series is a uniform grid (every frame
 * row, stride-downsampled), so the median recovers the effective bucket
 * spacing even when a few deltas straddle duplicated or dropped rows.
 */
export function medianSpacingMs(xs: number[]): number | null {
  const deltas: number[] = []
  for (let i = 1; i < xs.length; i++) {
    const delta = xs[i] - xs[i - 1]
    if (delta > 0) deltas.push(delta)
  }
  if (deltas.length === 0) return null
  deltas.sort((a, b) => a - b)
  return deltas[Math.floor(deltas.length / 2)]
}

/**
 * Null gaps up to this many bucket intervals get bridged when building the
 * band; anything longer is a genuine data hole and the band breaks there.
 */
export const BAND_BRIDGE_MAX_INTERVALS = 4

/**
 * Linearly interpolate across SHORT null runs (time span ≤ maxGapMs between
 * the surrounding non-null anchors), leaving long runs — and the unanchored
 * head/tail — null.
 *
 * The train series is a dense grid with `expected: null` at every bucket
 * where the target wasn't observed (cleaning-removed rows, sensor dropouts),
 * and the band plugin closes its polygon at every null — an isolated
 * surviving bucket draws a zero-width polygon, i.e. nothing. Bridging the
 * small holes lets the band read as one continuous envelope across the whole
 * experiment window (train and test alike), breaking only at real gaps.
 */
export function bridgeShortNullGaps(
  xs: number[],
  values: (number | null)[],
  maxGapMs: number
): (number | null)[] {
  const out = values.slice()
  let lastAnchor = -1
  for (let i = 0; i < out.length; i++) {
    const value = out[i]
    if (value === null || value === undefined) continue
    if (lastAnchor >= 0 && i - lastAnchor > 1) {
      const span = xs[i] - xs[lastAnchor]
      if (span > 0 && span <= maxGapMs) {
        const from = out[lastAnchor] as number
        const rise = (value - from) / span
        for (let j = lastAnchor + 1; j < i; j++) {
          out[j] = from + rise * (xs[j] - xs[lastAnchor])
        }
      }
    }
    lastAnchor = i
  }
  return out
}

/**
 * Expected ± residual_band for the selected trained method, shaped like a
 * sentinel band overlay so the chart plugin, tooltip, and legend readouts
 * all reuse the sentinel code paths unchanged. Sigma is residual/err_scale
 * (signed), matching the sentinel out-of-band affordances.
 *
 * Feature warm-up buckets at the head of the window are nulled out: the
 * service returns predictions for them, but they're artifacts of lag
 * features that don't exist yet, and drawing them swept the band far off
 * the target pen at the start of every experiment.
 */
export function buildExperimentBandOverlay(
  result: TrainResponse | null,
  methodKey: string
): ExperimentBandOverlay | null {
  if (!result?.ok) return null
  const method = result.methods?.[methodKey]
  const series = method?.series
  if (!method || !series?.ts?.length) return null

  const band =
    method.per_tag?.[result.target]?.residual_band ??
    method.band_meta?.residual_band
  if (band === null || band === undefined || !Number.isFinite(band)) {
    return null
  }
  const errScale =
    method.per_tag?.[result.target]?.err_scale ??
    method.band_meta?.err_scale ??
    null

  const xs = series.ts.map((ts) => new Date(ts).getTime())
  if (xs.some((x) => !Number.isFinite(x))) return null
  const expected = series.expected
  if (expected.length !== xs.length) return null

  // Time-based trim (not index-based): the series is stride-downsampled
  // server-side, so "first N rows" is unknowable here, but the warm-up SPAN
  // is fixed by the feature knobs and the native interval.
  const warmupEnd = xs[0] + warmupMs(result)
  const trimmed = expected.map((value, i) => (xs[i] < warmupEnd ? null : value))

  // Bridge short null holes so the band draws as one continuous envelope
  // (see bridgeShortNullGaps). Only the band GEOMETRY is bridged: sigma (and
  // therefore the out-of-band affordances) keys off the un-bridged values,
  // so interpolated buckets can never read as scored.
  const spacing = medianSpacingMs(xs)
  const bridged =
    spacing !== null
      ? bridgeShortNullGaps(xs, trimmed, spacing * BAND_BRIDGE_MAX_INTERVALS)
      : trimmed

  return {
    targetSensorId: result.target,
    sentinelName: `Experiment · ${method.label ?? methodKey}`,
    method: methodKey,
    label: t`${{ value182_16: result.target }} ±band (experiment)`,
    xs,
    upper: bridged.map((value) => (value === null ? null : value + band)),
    lower: bridged.map((value) => (value === null ? null : value - band)),
    center: bridged,
    actual: series.actual ?? xs.map(() => null),
    sigma: xs.map((_, i) => {
      if (trimmed[i] === null) return null
      const r = series.residual?.[i]
      if (r === null || r === undefined || !errScale) return null
      return r / errScale
    }),
    // The experiment lab has no persisted train/test split to mark.
    trainedSplitMs: null,
    modelCreatedMs: null,
    windowStartMs: null,
  }
}

/**
 * The y-extent the target pen's scale must reach for the band to fit
 * on-plot, percentile-guarded (1%–99% of finite edge values) so a handful of
 * deranged predictions (driver outages center-fill the model's inputs) can't
 * squash the pen into a ribbon. The pen's own data range is merged in by the
 * caller — this only ever widens.
 */
export function experimentBandScaleExtent(
  overlay: ExperimentBandOverlay
): { min: number; max: number } | null {
  const edges: number[] = []
  for (const value of overlay.upper) {
    if (value !== null && Number.isFinite(value)) edges.push(value)
  }
  for (const value of overlay.lower) {
    if (value !== null && Number.isFinite(value)) edges.push(value)
  }
  if (edges.length === 0) return null
  edges.sort((a, b) => a - b)
  const at = (q: number) =>
    edges[Math.min(edges.length - 1, Math.max(0, Math.floor(q * edges.length)))]
  return { min: at(0.01), max: at(0.99) }
}

/** One point where the actual left expected ± band, for chart dots + tab list. */
export interface ExperimentOutOfBandPoint {
  /** Epoch ms. */
  x: number
  actual: number
  expected: number
  /** |actual − expected| / band — ≥ 1 by construction; sort key for "worst". */
  severity: number
}

/**
 * Every band bucket whose actual value falls outside expected ± band,
 * time-ascending. The chart decimates for drawing; the tab uses the full
 * list for the count and the worst offenders.
 */
export function buildOutOfBandPoints(
  overlay: ExperimentBandOverlay | null
): ExperimentOutOfBandPoint[] {
  if (!overlay) return []
  const points: ExperimentOutOfBandPoint[] = []
  for (let i = 0; i < overlay.xs.length; i++) {
    const actual = overlay.actual[i]
    const upper = overlay.upper[i]
    const lower = overlay.lower[i]
    const expected = overlay.center[i]
    if (
      actual === null ||
      actual === undefined ||
      upper === null ||
      upper === undefined ||
      lower === null ||
      lower === undefined ||
      expected === null ||
      expected === undefined
    ) {
      continue
    }
    if (actual >= lower && actual <= upper) continue
    const band = (upper - lower) / 2
    points.push({
      x: overlay.xs[i],
      actual,
      expected,
      severity: band > 0 ? Math.abs(actual - expected) / band : Infinity,
    })
  }
  return points
}

/**
 * Where the test split begins, as an epoch-ms boundary for the chart's
 * demarcation line: the first series point the train response marked with
 * the "test" role. Null when the method has no test rows.
 */
export function experimentTestBoundaryMs(
  result: TrainResponse | null,
  methodKey: string
): number | null {
  if (!result?.ok) return null
  const series = result.methods?.[methodKey]?.series
  if (!series?.ts?.length || !series.role?.length) return null
  const index = series.role.findIndex((role) => role === 'test')
  if (index < 0) return null
  const ms = new Date(series.ts[index]).getTime()
  return Number.isFinite(ms) ? ms : null
}

/**
 * Removed-point marker series per tag from the data preview, for the red-X
 * overlay on the main chart (replacing the lab's per-tag Plotly charts).
 */
export function buildRemovedPointOverlays(
  preview: DataPreviewResponse | null
): ExperimentRemovedPoints[] {
  if (!preview?.ok || !preview.removed_points) return []
  const overlays: ExperimentRemovedPoints[] = []
  for (const [tag, removed] of Object.entries(preview.removed_points)) {
    if (!removed?.ts?.length) continue
    const xs = removed.ts.map((ts) => new Date(ts).getTime())
    if (xs.some((x) => !Number.isFinite(x))) continue
    overlays.push({
      sensorId: tag,
      xs,
      values: removed.value ?? [],
      reasons: removed.reason,
    })
  }
  return overlays
}

/**
 * Order-sensitive checksum over removed-point series, for the chart's plugin
 * structural signature (same role as `sentinelOverlayValuesDigest`): the
 * plugin closes over these arrays, so a changed preview must recreate it.
 */
export function removedPointsDigest(
  overlays: ExperimentRemovedPoints[]
): number {
  const bits = new DataView(new ArrayBuffer(8))
  let hash = 0
  const mix = (value: number | null | undefined) => {
    bits.setFloat64(0, value === null || value === undefined ? NaN : value)
    hash = Math.imul(hash ^ bits.getInt32(0), 2246822519)
    hash = Math.imul(hash ^ bits.getInt32(4), 2654435761)
  }
  for (const overlay of overlays) {
    mix(overlay.sensorId.length)
    for (let i = 0; i < overlay.xs.length; i++) {
      mix(overlay.xs[i])
      mix(overlay.values[i])
    }
  }
  return hash
}
